/**
 * Transition bridge: lets a not-yet-native mutation feed the overlay.
 *
 * A native connection mutation puts its operation straight into the mutation cache (see
 * `useConnectionMutation`). A mutation whose server write still runs through Relay's
 * `commitMutation` instead **dispatches** `optimistic`/`completed`/`error` events carrying
 * its `ConnectionOperation`; a single `useConnectionMutationBridge` per connection mirrors
 * each in-flight operation into the mutation cache. Because the coupling lives at the
 * mutation (it emits) and one bridge (it mirrors), every call site feeds the overlay with
 * no per-call-site wiring.
 *
 * Delete this module once every connection mutation is a native `useMutation`.
 */
import {useMutation} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useRef} from 'react'

import {type ConnectionOperation, connectionPathKey} from './connection-operations'
import {type ConnectionMutationVariables, connectionMutationKey} from './use-merged-connection'

const CONNECTION_MUTATION_EVENT = 'connection-mutation'

/**
 * One phase of a bridged (non-native) connection mutation. `mutationId` correlates the
 * `optimistic` → `completed`/`error` phases of a single invocation.
 */
export type ConnectionMutationEventDetail =
  | {phase: 'optimistic'; mutationId: string; ownerId: string; connectionPath: string[]; operation: ConnectionOperation}
  | {phase: 'completed'; mutationId: string; ownerId: string; connectionPath: string[]; operation: ConnectionOperation}
  | {phase: 'error'; mutationId: string; error: Error}

class ConnectionMutationEvent extends Event {
  readonly detail: ConnectionMutationEventDetail
  constructor(detail: ConnectionMutationEventDetail) {
    super(CONNECTION_MUTATION_EVENT)
    this.detail = detail
  }
}

/** The bus bridged mutations dispatch on and `useConnectionMutationBridge` reads. */
const connectionMutationEventTarget: EventTarget = new EventTarget()

let idCounter = 0
/** A fresh correlation id for one bridged mutation invocation. */
export function createConnectionMutationId(): string {
  return `conn-mut-${Date.now().toString(36)}-${(idCounter++).toString(36)}`
}

/**
 * Dispatch one phase of a bridged connection mutation. Producers call this from
 * their Relay `commitMutation` optimistic/onCompleted/onError callbacks so that
 * every call site of the mutation feeds the overlay.
 */
export function dispatchConnectionMutation(detail: ConnectionMutationEventDetail): void {
  connectionMutationEventTarget.dispatchEvent(new ConnectionMutationEvent(detail))
}

// Align an operation's own `connectionPath` to this bridge's key path before it enters the
// mutation cache (optimistic) or a settle fold (completed). `applyOperation(s)` locates the
// connection from the operation's own path, so a producer that emitted a mismatched path could
// otherwise mutate a sibling connection. `setFields` carries no connection path. Mirrors the
// same invariant `useConnectionMutation` enforces on its native producers.
function withKeyAlignedPath(operation: ConnectionOperation, connectionPath: string[]): ConnectionOperation {
  return operation.type === 'setFields' ? operation : {...operation, connectionPath}
}

type Deferred = {promise: Promise<void>; resolve: () => void; reject: (reason?: unknown) => void}
function createDeferred(): Deferred {
  let resolve!: () => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<void>((res, rej) => {
    resolve = res
    reject = rej
  })
  return {promise, resolve, reject}
}

type BridgeVariables = ConnectionMutationVariables & {mutationId: string; settled: Promise<void>}

// A settler plus the completion callback and the connection path captured at optimistic time,
// so a late terminal event folds into the connection the mutation *started* against — not
// whatever `onCompleted`/`connectionPath` this bridge instance has after e.g. navigating to
// another issue or being re-pointed at a different connection.
type Settler = Deferred & {
  onCompleted?: (operation: ConnectionOperation) => void | Promise<void>
  onOutstandingAbandoned?: () => void
  connectionPath: string[]
}

/**
 * Mirrors bridged connection-mutation events for one `{ownerId, connectionPath}` into the
 * mutation cache so `useMergedConnection` overlays them. `onCompleted` lets the consumer
 * fold the confirmed operation into its base before the pending operation drops
 * (settle-by-fold), avoiding a refetch race where the removed row flashes back.
 *
 * No-op when `enabled` is false: no event listener, no mutation-cache writes.
 */
export function useConnectionMutationBridge({
  ownerId,
  connectionPath,
  enabled = true,
  onCompleted,
  onOutstandingAbandoned,
}: {
  ownerId: string
  connectionPath: string[]
  enabled?: boolean
  /**
   * Fold the confirmed operation into `base` before the pending operation drops. May return a
   * promise (an async base refresh); the bridge awaits it before resolving so the overlay isn't
   * dropped before `base` reflects the write. Prefer a synchronous fold for a flash-free settle.
   */
  onCompleted?: (operation: ConnectionOperation) => void | Promise<void>
  /**
   * Called on unmount when mutations were still in flight. A terminal event dispatched while no
   * bridge is mounted is lost (`EventTarget` doesn't buffer), so those operations can't settle by
   * fold. The consumer must refresh its base instead — typically `invalidateQueries` for the
   * connection — so a remount reflects server truth rather than a stale, still-optimistic base.
   */
  onOutstandingAbandoned?: () => void
}): void {
  // mutationId → settler for its terminal event, so concurrent in-flight operations on the
  // same connection don't settle each other.
  const settlers = useRef(new Map<string, Settler>())

  const {mutate} = useMutation<void, unknown, BridgeVariables>({
    mutationKey: connectionMutationKey(ownerId, connectionPath),
    // Return the deferred captured at dispatch (carried on `variables`), not a map lookup.
    // TanStack runs this a microtask after `mutate()`; a terminal event in that gap can delete
    // the settler, and a lookup miss would resolve the mutation as successful and drop the
    // settler's rejection. Reading it from `variables` always reflects the real outcome.
    mutationFn: variables => variables.settled,
  })

  // Arrays are new each render; compare the connection by a stable, collision-free key.
  const pathKey = connectionPathKey(connectionPath)

  // `useEffectEvent` gives a stable handler that always sees the latest props
  // (ownerId, mutate, onCompleted) without re-subscribing the listener each render.
  const handleEvent = useEffectEvent((event: Event) => {
    const {detail} = event as ConnectionMutationEvent

    if (detail.phase === 'optimistic') {
      if (detail.ownerId !== ownerId || connectionPathKey(detail.connectionPath) !== pathKey) return
      // Register the settler before starting the mutation so a fast terminal event can't
      // arrive first. Capture the current `onCompleted`, `onOutstandingAbandoned` and
      // `connectionPath` so a settle (or abandonment) after this bridge re-points at another
      // connection still targets the connection the mutation *started* against.
      const deferred = createDeferred()
      settlers.current.set(detail.mutationId, {...deferred, onCompleted, onOutstandingAbandoned, connectionPath})
      // Carry the deferred promise on the variables so `mutationFn` returns this exact promise.
      // Even if a same-tick terminal event rejects it before `mutationFn` runs, returning it
      // there attaches TanStack's handler in the same microtask checkpoint — so the mutation
      // records the real outcome and the rejection is never unhandled.
      mutate({
        operation: withKeyAlignedPath(detail.operation, connectionPath),
        mutationId: detail.mutationId,
        settled: deferred.promise,
      })
      return
    }

    // completed/error: settle a mutation this bridge started.
    const settler = settlers.current.get(detail.mutationId)
    if (settler) {
      settlers.current.delete(detail.mutationId)
      if (detail.phase === 'completed') {
        // Align to the path captured at optimistic time, not this bridge's current
        // `connectionPath` (which may have changed if it re-pointed mid-flight). Await the fold
        // before resolving: `onCompleted` is typed `void` but may return a promise (an async
        // base refresh), and resolving first would drop the optimistic operation before `base`
        // reflects the write, flashing stale data. If the fold throws or rejects, settle the
        // deferred (reject) instead — otherwise this settler is already removed from the map, so
        // the mutation would stay `pending` forever (stale overlay) and unmount cleanup couldn't
        // reject it. Rejecting drops it from the pending set; the query refetches server truth.
        void (async () => {
          try {
            await settler.onCompleted?.(withKeyAlignedPath(detail.operation, settler.connectionPath))
            settler.resolve()
          } catch (error) {
            settler.reject(error)
          }
        })()
      } else {
        // Reject with the producer's own error so mutation-cache error handlers and diagnostics
        // see the actionable Relay/server cause, not a generic bridge message.
        settler.reject(detail.error)
      }
      return
    }

    // Orphan terminal event: its `optimistic` was registered on a previous bridge instance
    // that unmounted mid-flight (navigate away then back), so there's no settler. A
    // `completed` for this connection must still fold via the current `onCompleted` —
    // otherwise a remount whose refetch raced ahead of the server keeps the stale row until
    // an unrelated refetch. `applyOperation` is idempotent, so a redundant fold is a no-op;
    // an orphan `error` carries no target and its pending operation is already gone.
    if (
      detail.phase === 'completed' &&
      detail.ownerId === ownerId &&
      connectionPathKey(detail.connectionPath) === pathKey
    ) {
      // No settler to resolve/reject here — this is an orphan completion. `onCompleted` may return
      // a promise (an async base refresh); await it in a try/catch so a rejection can't become an
      // unhandled error. If it fails the connection just reconciles on the next refetch/live update.
      void (async () => {
        try {
          await onCompleted?.(withKeyAlignedPath(detail.operation, connectionPath))
        } catch {
          // Swallow: nothing to reject for an orphan, and base converges on the next server sync.
        }
      })()
    }
  })

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const outstanding = settlers.current
    connectionMutationEventTarget.addEventListener(CONNECTION_MUTATION_EVENT, handleEvent, {
      signal: controller.signal,
    })
    return () => {
      controller.abort()
      // On unmount the terminal event would arrive with no listener, leaving these mutations
      // `pending` forever (a later mount would replay a stale removal). Reject them now so they
      // leave the pending set.
      //
      // A terminal event dispatched while no bridge is mounted is lost (`EventTarget` doesn't
      // buffer), so those operations can't settle by fold and the consumer's base is left stale
      // (a removed row would reappear on remount). Ask the consumer to refresh each affected
      // connection's base — e.g. invalidate its query. Each abandonment callback is captured per
      // settler (like `onCompleted`), so if the bridge was re-pointed while a mutation was still
      // in flight, the connection it *started* against is refreshed too — not just the latest.
      const abandonCallbacks = new Set<() => void>()
      for (const settler of outstanding.values()) {
        if (settler.onOutstandingAbandoned) abandonCallbacks.add(settler.onOutstandingAbandoned)
        settler.reject(new Error('connection mutation bridge unmounted'))
      }
      outstanding.clear()
      for (const refreshBase of abandonCallbacks) refreshBase()
    }
  }, [enabled])
}
